import CopyLinkButton from '@/components/Discord/CopyLinkButton'
import { useState } from 'react'
import styles from '@/components/Discord/DiscordDocument.module.css'
import DiscordDocument from '@/components/Discord/DiscordDocument'

function DiscordTermsPage() {
  const [language, setLanguage] = useState<'en' | 'cs'>('cs')

  const SectionWrapper: React.FC<{ number: string; title: string; children: React.ReactNode }> = ({
    number,
    title,
    children,
  }) => {
    return (
      <section id={number} className={styles.section}>
        <CopyLinkButton sectionId={number} language={language} className={styles.copyBtn} />
        <h2 data-section={number}>{title}</h2>
        {children}
      </section>
    )
  }

  return (
    <DiscordDocument
      language={language}
      onLanguageChange={setLanguage}
      heading={
        <div className={styles.titleSection}>
          <h1 className={styles.title}>
            {language === 'cs' ? 'Podmínky služby Discord' : "Discord's Terms of Service"}
          </h1>
        </div>
      }
      notice={
        <>
          <div className={styles.disclaimer}>
            <div className={styles.disclaimerCard} lang="cs">
              <h3>Důležité upozornění</h3>
              <p>
                Nejsme přidruženi ani spojeni s Discord Inc. Toto je pouze komunitní překlad
                oficiálních Discord Terms of Service dostupných na{' '}
                <a href="https://discord.com/terms" target="_blank" rel="noopener noreferrer">
                  https://discord.com/terms
                </a>
                .
              </p>
              <p>
                Při nesrovnalostech je rozhodující originál Discordu. Verzi, datum posledního
                porovnání a provedené změny uvádíme v záhlaví a historii dokumentu. Chybu v překladu
                nám můžete nahlásit na našem Discord serveru.
              </p>
            </div>
            <div className={styles.disclaimerCard} lang="en">
              <h3>Important Notice</h3>
              <p>
                We are not affiliated with or associated with Discord Inc. This is only a community
                translation of the official Discord Terms of Service available at{' '}
                <a href="https://discord.com/terms" target="_blank" rel="noopener noreferrer">
                  https://discord.com/terms
                </a>
                .
              </p>
              <p>
                The Discord original prevails if there is a discrepancy. The document header and
                history identify the edition, latest comparison, and changes. You can report
                translation issues on our Discord server.
              </p>
            </div>
          </div>
          <div className={styles.linkLegend}>
            <h3>{language === 'cs' ? 'Legenda odkazů:' : 'Link legend:'}</h3>
            <div className={styles.legendItem}>
              <a
                href="#"
                className={`${styles.legendLink} ${styles.discord}`}
                onClick={(e) => e.preventDefault()}
              >
                {language === 'cs' ? 'Modré odkazy' : 'Blue links'}
              </a>
              <span>
                {language === 'cs' ? '= oficiální stránky Discord' : '= official Discord websites'}
              </span>
            </div>
            <div className={styles.legendItem}>
              <a
                href="#"
                className={`${styles.legendLink} ${styles.internal}`}
                onClick={(e) => e.preventDefault()}
              >
                {language === 'cs' ? 'Žluté odkazy' : 'Yellow links'}
              </a>
              <span>
                {language === 'cs' ? '= naše přeložené stránky' : '= our translated pages'}
              </span>
            </div>
          </div>
        </>
      }
    >
      {language === 'cs' && (
        <details className={styles.quickSummary} id="quick-summary">
          <summary>
            <h2>Rychlé shrnutí podmínek</h2>
          </summary>
          <CopyLinkButton
            sectionId={'quick-summary'}
            language={language}
            className={styles.copyBtn}
          />

          <div className={styles.summaryGrid}>
            <div className={styles.summaryCard}>
              <h3>Věkové požadavky</h3>
              <ul>
                <li>Minimálně 13 let (nebo více podle vaší země)</li>
                <li>Souhlas rodiče nebo zákonného zástupce, pokud nejste zletilí</li>
                <li>Rodiče zodpovídají za aktivitu svých dětí</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Váš účet</h3>
              <ul>
                <li>Zodpovídáte za zabezpečení účtu</li>
                <li>Používejte silné heslo a 2FA</li>
                <li>Udržujte přesné kontaktní údaje</li>
                <li>Převod účtu vyžaduje předchozí písemný souhlas</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Váš obsah</h3>
              <ul>
                <li>Obsah zůstává váš</li>
                <li>Licence slouží k poskytování, vývoji a zlepšování služeb</li>
                <li>Musíte mít práva k obsahu, který sdílíte</li>
                <li>Discord může obsah odstranit podle těchto podmínek</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Placené služby</h3>
              <ul>
                <li>Základní funkce jsou zdarma</li>
                <li>Prémiové funkce vyžadují platbu</li>
                <li>Platí samostatné podmínky pro nákupy</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Řešení sporů</h3>
              <ul>
                <li>Nejprve neformální řešení sporu</li>
                <li>Rozhodčí dohoda pro rezidenty USA a Kanady</li>
                <li>
                  Odmítnutí rozhodčí dohody do 30 dnů od 29. září 2025 nebo první registrace, podle
                  pozdějšího data
                </li>
                <li>Výjimky a ochrana spotřebitelů v EU jsou uvedeny v části 16</li>
              </ul>
            </div>
            <div className={styles.summaryCard}>
              <h3>Omezení odpovědnosti</h3>
              <ul>
                <li>Služby poskytovány "jak jsou"</li>
                <li>Vyloučení záruk v rozsahu dovoleném zákonem</li>
                <li>Omezená odpovědnost za škody</li>
                <li>Povinnost odškodnění popisuje část 12</li>
              </ul>
            </div>
          </div>
          <p className={styles.summaryNote}>
            Toto je redakční shrnutí Allcomu. Úplný překlad je níže; při nesrovnalostech platí
            originál Discordu. Část 16 rozlišuje obecná pravidla sporů, spotřebitele v EU a rozhodčí
            řízení pro rezidenty USA a Kanady.
          </p>
        </details>
      )}

      {language === 'en' ? (
        <div className={styles.englishPlaceholder} lang="en">
          <p>Read the current official English Terms of Service on Discord:</p>
          <a
            href="https://discord.com/terms"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.officialLink}
          >
            https://discord.com/terms
          </a>
          <p className={styles.note}>Switch to Czech to read our translation of this edition.</p>
        </div>
      ) : (
        <>
          {/* Table of contents */}

          <div className={styles.termsBody}>
            {/* Introduction */}
            <div className={styles.introduction}>
              <p>
                Vítejte! Discord vám umožňuje budovat smysluplné vztahy kolem radosti z hraní her
                prostřednictvím hlasových, obrazových a textových funkcí. Jsme rádi, že jste tu.
              </p>
              <p>
                Tyto podmínky stanovují naše vzájemné právní závazky. Vztahují se na vaše používání
                našich služeb.
              </p>
              <p>
                <strong>DŮLEŽITÁ POZNÁMKA: Sekce s názvem "</strong>
                <a href="#16">
                  <strong>Řešení sporů mezi vámi a Discord</strong>
                </a>
                <strong>
                  " obsahuje rozhodčí doložku a zřeknutí se hromadné žaloby, které se vztahují na
                  všechny uživatele Discordu s bydlištěm v USA nebo Kanadě. Přečtěte si prosím tuto
                  sekci pozorně, protože může významně ovlivnit vaše zákonná práva, včetně vašeho
                  práva podat žalobu u soudu.
                </strong>
              </p>
              <p>
                Když v těchto podmínkách říkáme "Discord", "my", "nás" a "naše", máme na mysli
                Discord Inc., její dceřiné společnosti a její přidružené společnosti.
              </p>
              <p>
                Když v těchto podmínkách říkáme "služby", máme na mysli služby, aplikace, webové
                stránky a další produkty Discord.
              </p>
              <p>
                Když říkáme "vy" nebo "váš", máme na mysli vás. Pokud přistupujete k našim službám
                jménem právnické osoby (jako je váš zaměstnavatel), souhlasíte s tím, že máte
                oprávnění zavázat tuto entitu k těmto podmínkám a "vy" a "váš" bude odkazovat na
                tuto entitu.
              </p>
              <p>
                Máme také <a href="https://discord.com/privacy">Zásady ochrany osobních údajů</a>,{' '}
                <a href="/discord/guidelines">Pokyny komunity</a> a další zásady (
                <a href="https://support.discord.com/hc/articles/4420312247575">uvedené zde</a> nebo
                jinak vám zpřístupněné), které se vztahují na vaše používání našich služeb a jsou
                začleněny do těchto podmínek. Měli byste si tyto zásady přečíst—tvrdě jsme pracovali
                na tom, aby byly jednoduché a jasné, a obsahují důležité informace o vašem používání
                našich služeb. Pokud používáte naše API, SDK nebo jiné vývojářské služby či
                software, vztahují se na toto použití{' '}
                <a href="https://discord.com/developers/docs/legal">
                  Podmínky služby pro vývojáře Discord
                </a>{' '}
                a <a href="https://discord.com/developers/docs/policy">Zásady pro vývojáře</a>.{' '}
                <a href="https://discord.com/terms/paid-services-terms">
                  Podmínky placených služeb Discord
                </a>{' '}
                se vztahují na jakýkoli nákup, který provedete prostřednictvím Discord, a{' '}
                <a href="https://support.discord.com/hc/en-us/articles/5330075836311-Monetization-Terms">
                  Podmínky monetizace Discord
                </a>{' '}
                se vztahují na jakýkoli prodej, který provedete prostřednictvím Discordu. Používáním
                služeb Discordu s těmito podmínkami souhlasíte.
              </p>
              <p>
                Společně tato pravidla umožňují existenci Discord a jsou pro nás důležitá. Pokud se
                domníváte, že je ostatní nedodržují, dejte nám prosím vědět{' '}
                <a href="/discord/safety/reporting-abusive-behavior-to-discord">nahlášením</a>.
              </p>
            </div>

            {/* Section 1 */}
            <SectionWrapper number="1" title="Kdo jsme">
              <p>
                Jsme Discord! Discord Inc. sídlí na adrese 444 De Haro Street #200, San Francisco,
                CA 94107, USA. Discord Netherlands B.V. sídlí na adrese Schiphol Boulevard 195, 1118
                BG Schiphol, Nizozemsko.
              </p>
            </SectionWrapper>

            {/* Section 2 */}
            <SectionWrapper
              number="2"
              title="Věkové požadavky a odpovědnost rodičů a zákonných zástupců"
            >
              <p>
                Přístupem k našim službám potvrzujete, že vám je alespoň 13 let a splňujete
                minimální věk požadovaný právními předpisy ve vaší zemi. Naše služby nejsou navrženy
                ani určeny pro uživatele mladší 13 let. Můžeme podniknout další kroky, včetně
                využití služeb třetích stran, abychom zjistili, zda jste dostatečně staří na
                vytvoření účtu Discord nebo přístup k určitým funkcím či prostorům. Vytvořením účtu
                Discord nebo používáním našich služeb přijímáte tyto podmínky, souhlasíte, že jimi
                budete vázáni, a prohlašujete, že jste buď dosáhli věku zletilosti v místě svého
                bydliště, nebo že váš rodič či zákonný zástupce souhlasí s tím, že bude těmito
                podmínkami vázán vaším jménem. Pokud nevíte, zda jste v místě svého bydliště dosáhli
                zletilosti, nebo této části nerozumíte, požádejte o pomoc rodiče či zákonného
                zástupce a požádejte jej, aby si tyto podmínky přečetl s vámi. Pro vaši informaci
                vedeme{' '}
                <a href="/discord/server-safety/age-restriction">
                  seznam minimálních věkových hranic
                </a>{' '}
                ve světě, ale nemůžeme zaručit, že je vždy přesný. Pokud jste rodič nebo zákonný
                zástupce a dovolíte svému dítěti (které musí splňovat minimální věk pro vaši zemi)
                používat služby, přijímáte vy i nezletilé dítě tyto podmínky, souhlasíte, že jimi
                budete vázáni, a nesete odpovědnost za veškeré používání účtu Discord nebo služeb,
                včetně činnosti dítěte ve službách a jeho nákupů, ať už účet nezletilého existuje
                nyní, nebo bude vytvořen později.
              </p>
            </SectionWrapper>

            {/* Section 3 */}
            <SectionWrapper number="3" title="Co od nás můžete očekávat">
              <p>
                Discord poskytuje služby, které vám pomáhají spojit se s ostatními uživateli a
                komunitami Discordu. Uživatelé Discordu komunikují především prostřednictvím
                „serverů“, což jsou digitální prostory složené z různých typů kanálů. Textové kanály
                umožňují uživatelům komunikovat pomocí textových zpráv, obrázků, GIFů, emoji a
                dalších médií, která lze nahrát. Hlasové kanály umožňují komunikovat hlasem a/nebo
                streamovat video. Uživatelé mohou také komunikovat mezi sebou prostřednictvím
                přímých zpráv nebo s omezeným počtem uživatelů prostřednictvím skupinových přímých
                zpráv. Naše služby se neustále vyvíjejí a v budoucnu můžeme vytvořit i jiné typy
                prostorů!
              </p>
              <p>
                Uživatelé vytvářejí servery a vybírají si, ke kterým serverům se připojí a kdo jsou
                jejich „přátelé“. Všichni uživatelé musí dodržovat naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a> a další zásady. Na serverech však
                vlastníci a správci řídí oprávnění a další pravidla serveru, včetně stanovení
                požadavků na členství, vytváření vlastních rolí a určení, co mohou tyto role na
                serveru dělat. Vlastníci a správci také rozhodují, zda svůj server zpřístupní ve
                vyhledávání serverů (Server Discovery), zda zveřejní odkaz s pozvánkou na veřejných
                webech, zda zapnou funkce pro růst a bezpečnost komunity a zda přidají boty nebo
                jiné aplikace. Tato oprávnění se stejně jako velikost serveru mohou časem měnit.
              </p>
              <p>
                Discord je navržen tak, aby byla komunikace s lidmi, se kterými chcete mluvit, co
                nejjednodušší. Příspěvky se v textových kanálech zobrazují v pořadí, v jakém byly
                vytvořeny. Hlasová a obrazová komunikace probíhá v reálném čase. Můžeme vytvářet
                funkce, které vám pomohou tvořit obsah, komunikovat s ostatními, objevovat obsah či
                služby třetích stran nebo k nim přistupovat a snadněji se zapojovat do konverzací.
                Můžeme také zvýrazňovat obsah a aktivity dostupné na Discordu, které by vás mohly
                zajímat. To může zahrnovat možnost, abyste vy nebo jiní uživatelé propojili účet
                Discord a komunikovali na Discordu i mimo něj prostřednictvím služeb třetích stran
                (například{' '}
                <a href="https://support.discord.com/hc/en-us/articles/27893392334359-In-game-Features-Powered-by-Discord-FAQ">
                  funkcí ve hrách
                </a>
                ). Naše služby mohou rovněž zahrnovat určité funkce a obsah poskytované či
                sponzorované jinými subjekty nebo takové, které si můžete zakoupit od nás či od
                jiných subjektů (mohou se na ně vztahovat další{' '}
                <a href="https://discord.com/terms/paid-services-terms">podmínky</a>). Více o{' '}
                <a href="#5">obsahu</a> a <a href="#6">softwaru</a>, které mohou být součástí našich
                služeb, se dozvíte níže.
              </p>
              <p>
                Discord je navržen tak, aby spolupracoval s vaším zařízením a usnadnil sdílení toho,
                co právě děláte. Když je Discord spuštěný, můžeme rozpoznávat aktivní hry a další
                aplikace na vašem zařízení. Díky tomu můžete během hraní rychle zavolat příteli,
                streamovat dění na svém zařízení, sdílet svůj stav (včetně toho, jakou hru hrajete
                nebo jakou skladbu posloucháte, pokud propojíte hudební účet) a využívat další
                možnosti. Sdílení hraní a dalších stavů aktivity je základní součástí používání
                Discordu. Nastavení můžete kdykoli změnit a sami rozhodujete o tom, zda k Discordu
                připojíte další účty.
              </p>
              <p>
                Služby Discordu mohou být pro každého uživatele přizpůsobeny na základě různých
                faktorů, včetně jeho aktivity, předvoleb zvolených v Nastavení a serverů, do kterých
                patří, abyste viděli obsah a komunity, které by vás mohly zajímat (například funkce
                nebo aktivity na Discordu, naše propagační nabídky a sponzorovaný obsah). V
                Nastavení můžete ovládat, zda a do jaké míry Discord vaše prostředí přizpůsobuje.
              </p>
              <p>
                Discord bere vážně také bezpečnost uživatelů. Poskytujeme funkce a služby navržené
                tak, aby pomáhaly uživatele chránit. Některé funkce a nastavení mohou mít odlišné
                výchozí hodnoty podle vašeho věku nebo místa bydliště a některé nemusí být vůbec
                volitelné. Poskytujeme také nástroje a zdroje, například{' '}
                <a href="https://discord.com/safety-family-center">Rodinné centrum</a> a{' '}
                <a href="https://discord.com/safety-parents">
                  Průvodce pro rodiče a zákonné zástupce
                </a>
                , které jim pomáhají lépe porozumět fungování Discordu, tomu, jak jej mohou jejich
                dospívající děti používat, a způsobům, jak společně rozvíjet pozitivní chování na
                internetu. Nabízíme také funkce pro lepší přístupnost Discordu,{' '}
                <a href="https://discord.com/accessibility">popsané zde</a>.
              </p>
              <p>
                Aktivně vyvíjíme nové funkce a produkty, abychom Discord zlepšovali. V rámci tohoto
                úsilí můžeme přidávat nebo odebírat funkce, začít nabízet nové služby nebo některé
                služby přestat nabízet úplně (nebo jen na určitých místech či některým uživatelům),
                pokud již nedávají obchodní smysl nebo vytvářejí riziko pro Discord, naše uživatele
                či jiné třetí strany. Přestože se snažíme narušením provozu předcházet, nemůžeme
                zaručit, že nedojde k výpadku nebo změně služeb. V důsledku takových výpadků či změn
                nemusí být možné váš obsah znovu získat. Za takové výpadky ani změny služeb neneseme
                odpovědnost.
              </p>
            </SectionWrapper>

            {/* Section 4 */}
            <SectionWrapper number="4" title="Váš Discord účet">
              <p>
                Pro trvalý přístup ke službám si budete muset vytvořit Discord účet. Můžete zadat
                uživatelské jméno a heslo a způsob, jak vás kontaktovat (například e-mailovou adresu
                a/nebo telefonní číslo). Budete také muset zadat své datum narození. V některých
                případech můžete být požádáni o ověření vašeho účtu nebo poskytnutí dalších
                informací.
              </p>
              <p>
                Jste zodpovědní za zabezpečení svého účtu a souhlasíte s tím, že nás{' '}
                <a href="https://support.discord.com/hc/en-us/requests/new?ticket_form_id=360000029212">
                  okamžitě upozorníte
                </a>
                , pokud se domníváte, že váš účet byl kompromitován. Pokud používáte heslo, musí být
                silné a (důrazně) doporučujeme, abyste toto heslo používali pouze pro svůj Discord
                účet a povolili dvoufaktorové ověřování.
              </p>
              <p>
                Musíte vždy poskytovat Discord přesné informace a udržovat přesnost informací
                spojených s vaším účtem. Můžeme předpokládat, že jakákoli komunikace, kterou jsme
                obdrželi z vašeho účtu nebo souvisejících kontaktních informací, byla provedena vámi
                a že jakékoli nákupy provedené pomocí vašeho účtu byly provedeny vámi.
              </p>
              <p>
                Pokud ztratíte přístup ke svému účtu, budeme vás muset kontaktovat na e-mail nebo
                telefonní číslo spojené s vaším účtem. Pokud je váš účet kompromitován nebo již
                nemáte přístup k vašemu e-mailovému účtu nebo telefonnímu číslu, nemusíme být
                schopni obnovit váš přístup k vašemu účtu nebo serverům, které jste vytvořili.
              </p>
              <p>
                Souhlasíte s tím, že nebudete licencovat, prodávat, půjčovat nebo převádět svůj
                účet, uživatelské jméno Discord, vanity URL nebo jiný jedinečný identifikátor bez
                našeho předchozího písemného souhlasu. Vyhrazujeme si také právo smazat, změnit nebo
                znovu získat vaše uživatelské jméno, URL nebo jiný identifikátor.
              </p>
            </SectionWrapper>

            {/* Section 5 */}
            <SectionWrapper number="5" title="Obsah ve službách Discordu">
              <h4>Váš obsah</h4>
              <p>
                Když v těchto podmínkách říkáme „váš obsah“, myslíme tím vše, co přidáváte
                (nahráváte, zveřejňujete, sdílíte, streamujete atd.) do našich služeb. Patří sem
                text, odkazy, GIFy, emoji, fotografie, videa, dokumenty a další média. Pokud
                vytvoříme jiný způsob přidávání obsahu do služeb, zahrnuje to i tento způsob.
              </p>
              <p>
                Nemáte žádnou povinnost do služeb přidávat obsah. Pokud se jej přidat rozhodnete,
                odpovídáte za to, že na to máte právo, že máte právo udělit licence uvedené v této
                části podmínek a že je váš obsah v souladu se zákonem. Za žádný váš obsah neneseme
                odpovědnost a neodpovídáme ani za to, jak jej používají ostatní.
              </p>
              <p>
                Naše služby umožňují uživatelům přidávat obsah různými způsoby, včetně přímých
                zpráv, skupinových přímých zpráv a malých i velkých serverů. Některé servery mohou
                být rozsáhlejšími prostory, a pokud v nich sdílíte obsah, je pravděpodobnější, že k
                němu bude mít přístup mnoho lidí. Některé servery jsou například dostupné v části
                aplikace Server Discovery a k připojení nepotřebujete odkaz s pozvánkou. Jiní
                vlastníci serverů mohou odkaz s pozvánkou zveřejnit na veřejných webech. Do těchto
                prostorů může vstoupit kdokoli. Mějte na paměti, že oprávnění nastavují vlastníci či
                správci serverů a mohou se časem měnit. Uvědomujte si prosím, kde na Discordu obsah
                zveřejňujete, při vstupu do konkrétního prostoru a zveřejňování obsahu se seznamte s
                příslušnými oprávněními serveru a zvolte pro sebe a svůj obsah vhodný prostor,
                funkce a nastavení.
              </p>
              <p>
                Váš obsah zůstává váš, ale používáním Discordu nám k němu udělujete licenci (tedy
                určité oprávnění). Váš obsah může být chráněn právy duševního vlastnictví. Tato
                práva nevlastníme. Používáním našich služeb nám však udělujete licenci k užívání,
                rozmnožování, rozšiřování, vytváření odvozených děl, zobrazování a předvádění vašeho
                obsahu. Práva, která touto licencí udělujete, slouží výhradně k poskytování, vývoji
                a zlepšování našich služeb v rozsahu dovoleném platnými právními předpisy. Tato
                licence je celosvětová, nevýhradní (takže svůj obsah můžete nadále licencovat i
                jiným), bezúplatná (za licenci se neplatí žádné poplatky), umožňuje udělování
                podlicencí a je převoditelná. Více o tom, jak váš obsah používáme, najdete v našich{' '}
                <a href="https://discord.com/privacy">Zásadách ochrany osobních údajů</a>.
              </p>
              <p>
                Vyhrazujeme si právo váš obsah zablokovat, odstranit a/nebo trvale smazat, pokud
                dospějeme k závěru, že porušuje tyto podmínky, naše{' '}
                <a href="/discord/guidelines">Pokyny komunity</a>, další zásady nebo platné právní
                předpisy, případně pokud jeho další zpřístupňování vytváří riziko pro Discord nebo
                nepříznivě ovlivňuje zkušenost či zájmy ostatních uživatelů Discordu. Souhlasíte s
                tím, že váš obsah můžeme zpřístupnit třetím stranám mimo služby, pokud se domníváme,
                že nám to ukládá zákon či předpis nebo že je to nutné k zabránění újmě jiné osobě
                nebo k ochraně práv a majetku Discordu.
              </p>
              <p>
                <a href="https://support.discord.com/hc/en-us/requests/new?ticket_form_id=360000029212">
                  Vítáme zpětnou vazbu
                </a>{' '}
                k našim službám. Zasláním zpětné vazby nám udělujete nevýhradní, časově neomezenou,
                neodvolatelnou a převoditelnou licenci k používání této zpětné vazby a nápadů z ní
                vycházejících bez jakýchkoli omezení, uvedení vašeho autorství nebo odměny.
              </p>
              <h4>Obsah Discordu</h4>
              <p>
                Naše služby zahrnují také obsah, který patří nám, například design našich aplikací a
                webů, naše umělecká díla a obrázky a texty, které jsme vytvořili. Náš software
                můžete používat způsobem uvedeným v těchto podmínkách. Naše ochranné známky (nebo
                jiná označení značky) a autorsky chráněná díla můžete používat pouze v rozsahu
                povoleném našimi{' '}
                <a href="https://discord.com/branding">Pravidly používání značky</a> nebo s naším
                předchozím písemným souhlasem. Veškerá práva duševního vlastnictví k našemu obsahu
                si ponecháváme.
              </p>
              <h4>Jiný obsah</h4>
              <p>
                <em>Obsah jiných lidí.</em> Naše služby vám mohou poskytovat také přístup k obsahu
                jiných lidí. Tento obsah nesmíte používat bez souhlasu dané osoby, ledaže to
                dovoluje zákon. Obsah jiných lidí patří jim a nemusí odrážet názory Discordu.
                Discord neschvaluje ani neověřuje přesnost či spolehlivost obsahu sdíleného
                uživateli Discordu. Usilujeme o to, aby byl Discord bezpečným, pozitivním a
                vstřícným místem pro všechny, ale nemůžeme vždy zabránit tomu, abyste se setkali s
                obsahem, který považujete za nevhodný nebo urážlivý. Souhlasíte s tím, že za újmu
                způsobenou takovým obsahem neponeseme odpovědnost. Můžete{' '}
                <a href="/discord/safety/reporting-abusive-behavior-to-discord">nahlásit obsah</a>,
                který podle vás porušuje některou z našich zásad. Máme právo, nikoli však povinnost,
                taková hlášení přezkoumat a podle svého uvážení obsah zablokovat nebo odstranit.
              </p>
              <p>
                <em>Funkce a obsah třetích stran.</em> Naše služby mohou rovněž obsahovat odkazy na
                weby, funkce, aplikace nebo jiný obsah třetích stran či umožňovat přístup k nim.
                Může jít i o sponzorovaný obsah (například Questy nebo jiné sponzorované formáty).
                Za obsah ani služby dostupné od těchto třetích stran neneseme odpovědnost.
              </p>
            </SectionWrapper>

            {/* Section 6 */}
            <SectionWrapper number="6" title="Software ve službách Discordu">
              <p>
                <em>Licence k našemu softwaru.</em> Některé naše služby umožňují stáhnout klientský
                software. Pokud dodržujete tyto podmínky, udělujeme vám celosvětovou, nevýhradní,
                osobní, nepřevoditelnou a nepostupitelnou licenci bez práva udělovat podlicence ke
                stažení, instalaci a spouštění tohoto softwaru výhradně za účelem přístupu k našim
                službám.
              </p>
              <p>
                Nesmíte kopírovat, upravovat, vytvářet odvozená díla založená na našem softwaru nebo
                službách, ani náš software či služby rozšiřovat, prodávat, pronajímat nebo
                poskytovat na základě podlicence. Rovněž nesmíte provádět zpětnou analýzu nebo
                dekompilaci našeho softwaru či služeb, pokoušet se o ni ani s ní někomu pomáhat,
                ledaže máte náš písemný souhlas nebo to dovoluje platné právo.
              </p>
              <p>
                Přestože vám tuto licenci udělujeme, ponecháváme si veškerá práva duševního
                vlastnictví k našemu softwaru a službám.
              </p>
              <p>
                <em>Open source.</em> Některé služby Discordu{' '}
                <a href="https://discord.com/acknowledgements">zahrnují software</a> podléhající
                samostatným{' '}
                <a href="https://discord.com/licenses">licenčním podmínkám open source</a>.
                Používání těchto služeb je podmíněno dodržováním takových licenčních podmínek, pokud
                se na ně vztahují. Doporučujeme je prostudovat, protože některé licence mohou mít
                výslovně přednost před těmito podmínkami.
              </p>
              <p>
                <em>Služby třetích stran.</em> Discord vám může umožnit přístup k aplikacím, botům
                nebo jiným produktům, funkcím či službám vyvinutým třetími stranami („služby třetích
                stran“). Třetí strany mohou například vytvářet aplikace pro moderování obsahu nebo
                interaktivní hry, které správci serverů mohou přidat na své servery na Discordu,
                nebo do svých služeb (například her hraných mimo Discord) začlenit určité funkce
                zajišťované Discordem (například zasílání zpráv). Sami si volíte, zda budete služby
                třetích stran používat a zda se zapojíte do serverů na Discordu nebo jiných
                prostorů, které je zahrnují. Předtím si přečtěte podmínky a zásady těchto třetích
                stran, protože upravují používání jejich služeb. Přestože tyto třetí strany musí
                dodržovat všechny zásady, které se na ně vztahují (což může zahrnovat tyto podmínky,
                naše <a href="/discord/guidelines">Pokyny komunity</a>,{' '}
                <a href="https://discord.com/developers/docs/legal">Podmínky služby pro vývojáře</a>{' '}
                a <a href="https://discord.com/developers/docs/policy">Zásady pro vývojáře</a>),
                Discord za služby třetích stran nenese odpovědnost. Třetí strany řídí provoz,
                funkčnost a dostupnost svých služeb, včetně toho, zda je zpřístupní v našich
                rozhraních pro objevování služeb. Discord za služby třetích stran neručí,
                neschvaluje je a nenese za ně odpovědnost. Aplikace třetích stran (včetně botů a
                Activities), které podle vás porušují některou z našich příslušných zásad, můžete
                nahlásit prostřednictvím tohoto{' '}
                <a href="https://support.discord.com/hc/en-us/requests/new?ticket_form_id=12275528604823">
                  formuláře
                </a>{' '}
                nebo možností hlášení, které zpřístupňujeme v našich službách. Máme právo, nikoli
                však povinnost, tato hlášení přezkoumat a podle vlastního uvážení přijmout opatření
                k vynucování pravidel.
              </p>
            </SectionWrapper>

            {/* Section 7 */}
            <SectionWrapper number="7" title="Autorská práva">
              <p>
                Respektujeme duševní vlastnictví ostatních a očekáváme, že naši uživatelé budou
                dělat totéž. Informace o tom, jak podat stížnost na porušení autorských práv,
                najdete v našich{' '}
                <a href="https://support.discord.com/hc/articles/4410339349655">
                  Zásadách autorských práv a duševního vlastnictví
                </a>
                .
              </p>
            </SectionWrapper>

            {/* Section 8 */}
            <SectionWrapper number="8" title="Placené služby Discord">
              <p>
                Nebudeme vám účtovat poplatek za používání základních funkcí našich služeb, ale
                můžete si zaplatit za další funkce a produkty.{' '}
                <a href="https://discord.com/terms/paid-services-terms">
                  Podmínky placených služeb
                </a>{' '}
                Discord se také vztahují na jakýkoli nákup, který provedete pomocí podporovaných
                platebních toků Discord, a můžete být také požádáni o souhlas se samostatnými
                podmínkami, jako jsou naše{' '}
                <a href="https://support.discord.com/hc/en-us/articles/5330075836311-Monetization-Terms">
                  Podmínky monetizace
                </a>
                , před nákupem nebo prodejem nových nabídek prostřednictvím Discord.
              </p>
            </SectionWrapper>

            {/* Section 9 */}
            <SectionWrapper number="9" title="Omezení vašeho používání služeb Discordu">
              <p>
                Při používání našich služeb musíte dodržovat tyto podmínky a všechny platné zákony,
                pravidla a předpisy a používat služby pouze k oprávněným a přijatelným účelům.
                Musíte rovněž dodržovat naše <a href="/discord/guidelines">Pokyny komunity</a> a
                další zásady, které obsahují podrobnější pravidla pro váš obsah a chování při
                používání Discordu i způsob jejich vynucování na Discordu.
              </p>
              <p>
                V zásadě nesmíte dělat nic z následujícího, pokoušet se o to, ani k tomu ostatní
                vybízet nebo jim s tím pomáhat:
              </p>
              <ul role="list">
                <li>
                  <strong>Nepoužívejte služby k ubližování sobě nebo ostatním.</strong> To mimo jiné
                  zahrnuje pokusy získat přístup k účtu jiného uživatele nebo neveřejným částem
                  služeb; porušování práv duševního vlastnictví či jiných majetkových práv
                  ostatních; vykořisťování, obtěžování, šikanu, spamování, automatické zasílání
                  zpráv nebo automatické vytáčení hovorů lidem prostřednictvím našich služeb; a
                  používání našich služeb k plánování nebo způsobení újmy jinému člověku ve
                  skutečném světě.
                </li>
                <li>
                  <strong>Nepoužívejte služby k poškozování Discordu.</strong> To mimo jiné zahrnuje
                  pokusy získat přístup k našim systémům, jejich úmyslné přetěžování nebo napadání;
                  automatizované získávání dat (scraping) z našich služeb bez našeho písemného
                  souhlasu, včetně použití robotů, spiderů, crawlerů, scraperů či jiných
                  automatických zařízení, postupů nebo softwaru; prodej, licencování či jiné
                  komerční využívání obsahu nebo dat získaných z našich služeb; přenos virů či
                  jiného škodlivého kódu do našich služeb; používání neschváleného softwaru určeného
                  k úpravě služeb; zneužívání či podvádění nás nebo našich platebních systémů;
                  kopírování, rozebírání či zpětnou analýzu našich služeb nebo používání našeho
                  duševního vlastnictví bez svolení; a zneužívání mechanismů hlášení nebo zákaznické
                  podpory.
                </li>
                <li>
                  <strong>Nepoužívejte služby k ničemu jinému nezákonnému.</strong> To zahrnuje
                  používání služeb k plánování či páchání trestného činu nebo k jakémukoli jinému
                  nezákonnému jednání.
                </li>
              </ul>
              <p>
                Doporučujeme vám{' '}
                <a href="/discord/safety/reporting-abusive-behavior-to-discord">nahlásit obsah</a>{' '}
                nebo chování, které podle vás tato omezení porušuje. Více o našem přístupu k
                bezpečnosti a moderování obsahu najdete v našem{' '}
                <a href="https://discord.com/safety/our-approach-to-content-moderation">
                  Centru bezpečnosti
                </a>
                .{' '}
                <strong>
                  Pokud jste vy nebo někdo jiný v bezprostředním nebezpečí, obraťte se na místní
                  policii. Upozorňujeme, že prostřednictvím našich služeb nelze kontaktovat policii
                  ani jiné záchranné složky.
                </strong>
              </p>
            </SectionWrapper>

            {/* Section 10 */}
            <SectionWrapper number="10" title="Trvání a ukončení smlouvy">
              <p>
                Vaše právo na ukončení. Můžete kdykoli a z jakéhokoli důvodu přestat používat služby
                Discord. Pro ukončení této smlouvy můžete smazat svůj Discord účet prostřednictvím
                stránky Nastavení v aplikaci Discord (ikona ozubeného kola vedle vašeho
                uživatelského jména) a přestat používat služby. Některá ustanovení těchto podmínek
                zůstanou v platnosti po ukončení, jak je uvedeno níže v části "Přetrvání".
              </p>
              <p>
                Deaktivace vašeho účtu omezuje zpracování vašich osobních údajů, jak je popsáno v
                našich <a href="https://discord.com/privacy">Zásadách ochrany osobních údajů</a>.
                Deaktivace vašeho účtu neukončuje tuto smlouvu.
              </p>
              <p>
                Naše právo na ukončení. S výhradou platného práva si vyhrazujeme právo pozastavit
                nebo ukončit váš účet a/nebo váš přístup k některým nebo všem našim službám s
                oznámením nebo bez něj, podle našeho uvážení z jakéhokoli důvodu nebo z
                následujících důvodů:
              </p>
              <ul role="list">
                <li>
                  Porušíte tyto podmínky nebo k jejich porušení vybízíte ostatní či jim s ním
                  pomáháte (včetně našich <a href="/discord/guidelines">Pokynů komunity</a>, dalších
                  zásad nebo dodatečných podmínek vztahujících se na konkrétní služby).
                </li>
                <li>
                  Jsme povinni tak učinit, abychom vyhověli právním požadavkům nebo soudnímu
                  příkazu.
                </li>
                <li>
                  Důvodně se domníváme, že ukončení je nezbytné k zabránění újmě vám, nám, jiným
                  uživatelům nebo třetím stranám.
                </li>
                <li>Váš účet byl neaktivní déle než dva roky.</li>
                <li>
                  Pokračování v aktivitě vašeho účtu, poskytování přístupu k některým nebo všem
                  službám nebo hostování vašeho obsahu vytváří riziko pro Discord, jiné uživatele
                  nebo třetí strany.
                </li>
              </ul>
              <p>
                Pokud je to však přiměřené nebo vyžaduje-li to platný zákon, poskytneme vám předem
                upozornění.
              </p>
            </SectionWrapper>

            {/* Section 11 */}
            <SectionWrapper number="11" title="Odvolání">
              <p>
                Vážíme si transparentnosti a tvrdě pracujeme na tom, abychom vám poskytli kontext
                pro rozhodnutí, která činíme. Proti jakémukoli vynucovacímu opatření, které
                podnikneme podle těchto podmínek nebo jiných zásad, včetně ukončení, pozastavení
                nebo odstranění obsahu, se můžete odvolat prostřednictvím tohoto{' '}
                <a href="https://dis.gd/request">formuláře</a> nebo dostupných{' '}
                <a href="/discord/safety/reporting-abusive-behavior-to-discord">
                  možností v aplikaci
                </a>
                . Pokud máte bydliště v Evropské unii, musíte odvolání podat do šesti měsíců ode
                dne, kdy vám bylo příslušné rozhodnutí oznámeno.
              </p>
            </SectionWrapper>

            {/* Section 12 */}
            <SectionWrapper number="12" title="Odškodnění">
              <p>
                Odškodníte Discord a členy jeho vedení a správních orgánů, zaměstnance a zástupce a
                ochráníte je před veškerými nároky, závazky, škodami a náklady (včetně přiměřených
                nákladů na právní a účetní služby) souvisejícími s (a) vaším přístupem k našim
                službám či službám třetích stran nebo jejich používáním, (b) vaším obsahem, (c)
                porušením těchto podmínek z vaší strany nebo (d) vaší nedbalostí či úmyslným
                protiprávním jednáním.
              </p>
            </SectionWrapper>

            {/* Section 13 */}
            <SectionWrapper number="13" title="Služby „TAK JAK JSOU“">
              <p>
                Usilujeme o poskytování skvělých služeb, ale nemůžeme zaručit žádnou minimální
                úroveň jejich kvality (ta může záviset i na okolnostech mimo naši kontrolu,
                například na kvalitě vašeho internetového připojení a vybavení). V NEJŠIRŠÍM ROZSAHU
                DOVOLENÉM ZÁKONEM DISCORD, JEHO PŘIDRUŽENÉ SPOLEČNOSTI A JEJICH DODAVATELÉ
                NEPOSKYTUJÍ OHLEDNĚ SLUŽEB ŽÁDNÉ VÝSLOVNÉ ANI PŘEDPOKLÁDANÉ ZÁRUKY. SLUŽBY SE
                POSKYTUJÍ „TAK JAK JSOU“. VYLUČUJEME TAKÉ JAKÉKOLI PŘEDPOKLÁDANÉ ZÁRUKY
                OBCHODOVATELNOSTI, VHODNOSTI PRO URČITÝ ÚČEL, NERUŠENÉHO UŽÍVÁNÍ A NEPORUŠOVÁNÍ
                PRÁV, STEJNĚ JAKO ZÁRUKY VYPLÝVAJÍCÍ Z DOSAVADNÍHO OBCHODNÍHO STYKU NEBO OBCHODNÍCH
                ZVYKLOSTÍ. PRÁVNÍ PŘEDPISY NĚKTERÝCH JURISDIKCÍ ČI STÁTŮ OMEZENÍ PŘEDPOKLÁDANÝCH
                ZÁRUK NEUMOŽŇUJÍ. POKUD PODLE PRÁVA VAŠÍ JURISDIKCE NELZE TYTO ZÁRUKY VYLOUČIT,
                OMEZUJEME JEJICH DOBU TRVÁNÍ A NÁROKY Z NICH VYPLÝVAJÍCÍ V NEJŠIRŠÍM ROZSAHU, KTERÝ
                TYTO PRÁVNÍ PŘEDPISY DOVOLUJÍ.
              </p>
            </SectionWrapper>

            {/* Section 14 */}
            <SectionWrapper number="14" title="Poplatky za data">
              <p>
                Jste zodpovědní za jakékoli mobilní poplatky, které vám mohou vzniknout při
                používání našich služeb. To zahrnuje poplatky za data a poplatky za zasílání zpráv,
                jako jsou SMS, MMS nebo jiné protokoly nebo technologie zasílání zpráv. Pokud si
                nejste jisti, jaké poplatky vám mohou být účtovány, měli byste se před použitím
                našich služeb zeptat svého poskytovatele mobilních služeb.
              </p>
            </SectionWrapper>

            {/* Section 15 */}
            <SectionWrapper number="15" title="Omezení odpovědnosti">
              <p>
                NEVYLUČUJEME ANI NEOMEZUJEME NAŠI ODPOVĚDNOST VŮČI VÁM TAM, KDE BY TO BYLO
                NEZÁKONNÉ. V JURISDIKCÍCH, KDE NEJSOU POVOLENY NÍŽE UVEDENÉ TYPY VYLOUČENÍ, JSME VÁM
                ODPOVĚDNÍ POUZE ZA ZTRÁTY A ŠKODY, KTERÉ JSOU PŘIMĚŘENĚ PŘEDVÍDATELNÝM DŮSLEDKEM
                TOHO, ŽE JSME NEVYNALOŽILI PŘIMĚŘENOU PÉČI A ODBORNOST NEBO NAŠEHO PODSTATNÉHO
                PORUŠENÍ NAŠÍ SMLOUVY S VÁMI. TENTO ODSTAVEC NEOVLIVŇUJE SPOTŘEBITELSKÁ PRÁVA,
                KTERÝCH SE NELZE VZDÁT NEBO OMEZIT ŽÁDNOU SMLOUVOU NEBO DOHODOU.
              </p>
              <p>
                INFORMACE PREZENTOVANÉ NA NAŠICH SLUŽBÁCH NEBO JEJICH PROSTŘEDNICTVÍM JSOU
                ZPŘÍSTUPNĚNY VÝHRADNĚ PRO INFORMAČNÍ ÚČELY. NEPOTVRZUJEME PŘESNOST, ÚPLNOST NEBO
                UŽITEČNOST INFORMACÍ. JAKÉKOLI SPOLÉHÁNÍ SE NA TAKOVÉ INFORMACE JE VÝHRADNĚ NA VAŠE
                VLASTNÍ RIZIKO.
              </p>
              <p>
                V JURISDIKCÍCH, KDE JSOU VYLOUČENÍ NEBO OMEZENÍ ODPOVĚDNOSTI POVOLENY, ANI DISCORD,
                JEHO PŘIDRUŽENÉ SPOLEČNOSTI, ANI NAŠI DODAVATELÉ ZAPOJENÍ DO VYTVÁŘENÍ, VÝROBY NEBO
                DODÁVÁNÍ SLUŽEB NEBUDOU ODPOVĚDNÍ, V MAXIMÁLNÍM ROZSAHU POVOLENÉM PODLE PLATNÉHO
                PRÁVA, ZA JAKÉKOLI VEDLEJŠÍ, ZVLÁŠTNÍ, EXEMPLÁRNÍ NEBO NÁSLEDNÉ ŠKODY NEBO ŠKODY ZA
                ZTRACENÉ ZISKY, ZTRACENÉ VÝNOSY, ZTRACENÉ ÚSPORY, ZTRACENÉ OBCHODNÍ PŘÍLEŽITOSTI,
                ZTRÁTU DAT NEBO DOBRÉ POVĚSTI, PŘERUŠENÍ SLUŽBY, POŠKOZENÍ POČÍTAČE NEBO SELHÁNÍ
                SYSTÉMU NEBO NÁKLADY NA NÁHRADNÍ SLUŽBY JAKÉHOKOLI DRUHU VYPLÝVAJÍCÍ Z TĚCHTO
                PODMÍNEK NEBO V SOUVISLOSTI S NIMI NEBO Z POUŽÍVÁNÍ NEBO NEMOŽNOSTI POUŽÍVAT SLUŽBY,
                AŤ UŽ NA ZÁKLADĚ ZÁRUKY, SMLOUVY, ZÁKONA, DELIKTU (VČETNĚ NEDBALOSTI), ODPOVĚDNOSTI
                ZA VÝROBEK NEBO JAKÉKOLI JINÉ PRÁVNÍ TEORIE A BEZ OHLEDU NA TO, ZDA DISCORD NEBO
                JEHO DODAVATELÉ BYLI INFORMOVÁNI O MOŽNOSTI TAKOVÝCH ŠKOD, I KDYŽ SE ZJISTÍ, ŽE
                OMEZENÁ NÁPRAVA UVEDENÁ V TĚCHTO PODMÍNKÁCH SELHALA VE SVÉM ZÁKLADNÍM ÚČELU.
              </p>
              <p>
                KROMĚ TYPŮ ODPOVĚDNOSTI, KTERÉ ZE ZÁKONA NEMŮŽEME OMEZIT (JAK JE POPSÁNO V TÉTO
                ČÁSTI), DISCORD OMEZUJE SVOU ODPOVĚDNOST VŮČI VÁM NA VĚTŠÍ Z (A) ČÁSTEK, KTERÉ JSTE
                NÁM ZAPLATILI VE TŘECH MĚSÍCÍCH PŘED PRVNÍM UPLATNĚNÍM NÁROKU NEBO (B) 100 USD (NEBO
                EKVIVALENT VE VAŠÍ MÍSTNÍ MĚNĚ).
              </p>
              <p>
                DISCORD NENÍ ODPOVĚDNÝ ZA CHOVÁNÍ NEBO OBSAH, AŤ UŽ ONLINE NEBO OFFLINE, JAKÉHOKOLI
                UŽIVATELE NAŠICH SLUŽEB.
              </p>
              <p>
                VÝŠE UVEDENÁ VYLOUČENÍ A OMEZENÍ ŠKOD JSOU ZÁKLADNÍMI PRVKY ZÁKLADU DOHODY MEZI
                DISCORD A VÁMI. OMEZENÍ ODPOVĚDNOSTI POPSANÉ VÝŠE SE PLNĚ VZTAHUJE NA OBYVATELE NEW
                JERSEY.
              </p>
            </SectionWrapper>

            {/* Section 16 */}
            <SectionWrapper number="16" title="Řešení sporů mezi vámi a Discordem">
              <p>
                PŘEČTĚTE SI TUTO ČÁST POZORNĚ. OVLIVŇUJE VAŠE ZÁKONNÁ PRÁVA, VČETNĚ PRÁVA PODAT
                ŽALOBU U SOUDU NEBO SE ÚČASTNIT SOUDNÍHO ŘÍZENÍ, A OMEZUJE VAŠE PRÁVA ŘEŠIT SPOR V
                RÁMCI HROMADNÉ NEBO ZÁSTUPNÉ ŽALOBY.
              </p>
              <p>
                <strong>Tato část se vztahuje na veškeré spory mezi vámi a Discordem.</strong>{' '}
                „Spory“ znamenají všechny neshody mezi námi, mimo jiné nároky vzniklé z jakéhokoli
                aspektu našeho vzájemného vztahu, těchto podmínek nebo vašeho používání služeb či s
                nimi související, ať už vycházejí ze smlouvy, deliktu, zákona, podvodu, nepravdivého
                prohlášení nebo jiného právního základu (jednotlivě „Spor“, společně „Spory“).
              </p>
              <p>
                <strong>„Neformální řešení sporů“.</strong> Většinu Sporů lze vyřešit neformálně.
                Máte-li problém se službami nebo Discordem, souhlasíte s tím, že se na nás obrátíte
                a před zahájením soudního či rozhodčího řízení v dobré víře vynaložíte úsilí k
                neformálnímu vyřešení Sporu jednáním. K tomu je nutné zaslat na{' '}
                <a href="mailto:disputes@discordapp.com">disputes@discordapp.com</a> písemné
                oznámení („Písemné oznámení“) o vašem problému, které musí obsahovat: (1) vaše
                jméno; (2) e-mailovou adresu nebo telefonní číslo spojené s vaším účtem Discord; (3)
                podrobný popis problému; a (4) způsob, jakým byste jej chtěli vyřešit. Oznámení musí
                být podáno individuálně. Vy osobně a váš právní zástupce, pokud jej máte, musíte být
                k dispozici pro telefonickou nebo videokonferenci za účelem vyřešení vašich nároků,
                ledaže Discord písemně uvede, že vaše osobní účast není vyžadována.
              </p>
              <p>
                Pokud se Spor nevyřeší do 60 dnů od obdržení Písemného oznámení, souhlasíte vy i
                Discord s tím, že zbývající Spor vyřešíte dalšími neformálními jednáními nebo podle
                některého z níže uvedených ustanovení o formálním řešení sporů.
              </p>
              <p>
                Tento postup Neformálního řešení sporů, včetně vaší osobní účasti na společném
                jednání, je nezbytným předpokladem a podmínkou zahájení jakéhokoli formálního řízení
                o řešení sporu. Vy a Discord („Strany“) souhlasíte, že po dobu, kdy se Strany
                účastní tohoto postupu Neformálního řešení sporů, se staví běh příslušných
                promlčecích lhůt a lhůt pro úhradu poplatků za podání či jiných lhůt. Strany dále
                souhlasí, že o tom, zda stěžující si Strana splnila požadavky tohoto postupu, včetně
                toho, zda bylo Písemné oznámení řádně podáno a obsahovalo všechny požadované
                informace, může před zahájením rozhodčího řízení rozhodnout soud.
              </p>
              <p>
                Pokud máte bydliště v Evropské unii, můžete být rovněž oprávněni předložit svou
                stížnost mechanismu mimosoudního řešení sporů („OCDS“) podle článku 21 DSA. Samotné
                podání prostřednictvím mechanismu OCDS bez zaslání Písemného oznámení na{' '}
                <a href="mailto:disputes@discordapp.com">disputes@discordapp.com</a> však nestaví
                běh příslušných promlčecích ani jiných lhůt.
              </p>
              <p>
                <strong>
                  Rozhodné právo, příslušnost soudů, vzdání se hromadných žalob a časové omezení.
                </strong>
              </p>
              <p>NENÍ-LI TO ZAKÁZÁNO PLATNÝM PRÁVEM:</p>
              <p>
                <strong>Volba práva.</strong> Tyto podmínky a všechny Spory mezi vámi a Discordem,
                ať už v rozhodčím nebo soudním řízení (pokud Spor podle těchto podmínek z jiného
                důvodu nepodléhá rozhodčímu řízení), se řídí právem Spojených států a právem
                Kalifornie bez ohledu na kolizní normy. Jinými slovy se nepoužije žádné ustanovení
                federálního nebo kalifornského práva, včetně kolizní zásady, které by stanovilo
                použití práva jiné jurisdikce než Spojených států nebo Kalifornie.
              </p>
              <p>
                <strong>Místní příslušnost a pravomoc soudů.</strong> Přijetím těchto podmínek vy a
                Discord souhlasíte s tím, že každý Spor, který nepodléhá rozhodčímu řízení, včetně
                soudních řízení souvisejících s rozhodčím řízením, například návrhu na jeho
                nařízení, přerušení soudního řízení do skončení rozhodčího řízení nebo potvrzení,
                změnu či zrušení rozhodčího nálezu či vydání rozsudku na jeho základě, musí být
                řešen výhradně příslušným federálním nebo státním soudem (včetně soudu pro drobné
                nároky) v okrese New Castle ve státě Delaware a žádným jiným soudem. Vy a Discord
                souhlasíte, že tyto soudy vůči vám vykonávají osobní jurisdikci v souvislosti s
                každým takovým Sporem.
              </p>
              <p>
                Jste-li spotřebitelem s bydlištěm v Evropské unii, tato doložka ani tyto podmínky
                obecně neovlivňují žádná kogentní spotřebitelská práva, která máte podle místního
                práva. Veškeré Spory vzniklé v souvislosti se službami a/nebo těmito podmínkami
                budou předloženy do výlučné pravomoci soudu v Amsterdamu v Nizozemsku, nebo, jste-li
                spotřebitelem, soudu v místě vašeho bydliště, nachází-li se v členském státě EU.
              </p>
              <p>
                <strong>Vzdání se hromadných žalob.</strong> Souhlasíte s tím, že jakýkoli Spor mezi
                vámi a Discordem, na který se z jakéhokoli důvodu nevztahuje níže uvedená „Dohoda o
                rozhodčím řízení“, můžete uplatnit pouze individuálně. Nemůžete uplatnit nárok jako
                žalobce nebo člen skupiny v hromadné, kolektivní či zástupné žalobě. Pokud Spor mezi
                vámi a Discordem podléhá rozhodčímu řízení, použijí se ustanovení „Dohody o
                rozhodčím řízení“, včetně ustanovení o hromadném rozhodčím řízení, rozhodčím řízení
                vedeném soukromým žalobcem ve veřejném zájmu, rozhodčím řízení zahrnujícím společné
                či sloučené nároky a projednávání v dávkách.
              </p>
              <p>
                <strong>Lhůta k uplatnění nároku.</strong> Žádná ze Stran nesmí bez ohledu na formu
                zahájit řízení vyplývající ze Sporu nebo s ním související později než jeden (1) rok
                po vzniku práva na uplatnění nároku.
              </p>
              <p>
                JSTE-LI REZIDENTEM USA NEBO KANADY, SOUHLASÍTE TAKÉ S NÁSLEDUJÍCÍ „DOHODOU O
                ROZHODČÍM ŘÍZENÍ“, KTERÁ OBSAHUJE USTANOVENÍ O POVINNÉM ROZHODČÍM ŘÍZENÍ. PŘEČTĚTE
                SI TUTO ČÁST POZORNĚ – MŮŽE VÝZNAMNĚ OVLIVNIT VAŠE ZÁKONNÁ PRÁVA, VČETNĚ PRÁVA PODAT
                ŽALOBU U SOUDU:
              </p>
              <p>
                <strong>Dohoda o rozhodčím řízení.</strong>
              </p>
              <p>
                „Dohoda o rozhodčím řízení“ zahrnuje všechna ustanovení následující po tomto nadpisu
                až po odstavec nazvaný „Změny této Dohody o rozhodčím řízení“ včetně.
              </p>
              <p>
                Teprve po účasti na postupu Neformálního řešení sporů stanoveném v těchto
                podmínkách, a pouze pokud se tímto úsilím nepodaří Spor vyřešit, může kterákoli
                Strana zahájit závazné rozhodčí řízení jako jediný způsob řešení Sporů, s výhradou
                ustanovení této Dohody o rozhodčím řízení a příslušných rozhodčích pravidel.
              </p>
              <p>
                Vy a Discord (společně „Strany“) souhlasíte s tím, že tato Dohoda o rozhodčím řízení
                je uzavřena v souvislosti s transakcí zahrnující obchod mezi státy a řídí se
                americkým federálním zákonem o rozhodčím řízení (Federal Arbitration Act, „FAA“), 9
                U.S.C. §§ 1–16.
              </p>
              <p>
                Tato Dohoda o rozhodčím řízení má být vykládána široce a vztahuje se na veškeré
                Spory mezi Stranami, včetně: (1) nároků, které vznikly, byly uplatněny nebo se
                týkají skutečností nastalých před vznikem této Dohody o rozhodčím řízení či jakékoli
                předchozí dohody; a (2) nároků, které mohou vzniknout po ukončení této Dohody o
                rozhodčím řízení. Tato Dohoda o rozhodčím řízení nahrazuje jakoukoli předchozí
                rozhodčí dohodu mezi vámi a Discordem.
              </p>
              <p>
                S výjimkou případů uvedených níže v odstavci „Výjimky“ má rozhodce, nikoli
                federální, státní či místní soud nebo správní orgán, výlučnou pravomoc rozhodovat o:
                (1) všech Sporech; (2) tom, zda nárok podléhá rozhodčímu řízení; a (3) sporech o
                úhradu administrativních poplatků nebo odměny rozhodce (včetně načasování plateb a
                prostředků nápravy při nezaplacení). Rozhodce je oprávněn přiznat jakoukoli nápravu,
                která by byla dostupná u soudu podle zákona nebo zásad ekvity. Rozhodce může v
                souladu s pravidly a postupy poskytovatele rozhodčího řízení ukládat sankce za
                zjevně bezdůvodné nároky či podání, která podle jeho závěru nebyla učiněna v dobré
                víře, a za nedodržení této Dohody o rozhodčím řízení nebo postupu Neformálního
                řešení sporů některou ze Stran.
              </p>
              <p>
                ROZHODČÍ ŘÍZENÍ ZNAMENÁ, ŽE SE VZDÁVÁTE PRÁVA NA SOUDNÍ ŘÍZENÍ PŘED POROTOU. V
                některých případech mohou náklady rozhodčího řízení převýšit náklady soudního sporu
                a možnost získávat důkazy od druhé strany (discovery) může být v rozhodčím řízení
                omezenější než u soudu. TÍMTO POTVRZUJETE A SOUHLASÍTE S TÍM, ŽE SE VY I DISCORD V
                NEJŠIRŠÍM ROZSAHU DOVOLENÉM ZÁKONEM VZDÁVÁTE PRÁVA NA SOUDNÍ ŘÍZENÍ PŘED POROTOU.
              </p>
              <p>
                <strong>Pravidla rozhodčího řízení.</strong> Pro obyvatele státu Kalifornie nebo pro
                hromadná rozhodčí řízení, jichž se účastní jakýkoli obyvatel Kalifornie, bude
                rozhodčí řízení spravovat ADR Services, Inc. („ADR Services“) (
                <a href="https://www.adrservices.com/">https://www.adrservices.com/</a>). Pro
                obyvatele všech ostatních států a pro hromadná rozhodčí řízení bez účasti obyvatel
                Kalifornie bude řízení spravovat National Arbitration and Mediation („NAM“) a
                rozhodovat jediný rozhodce. Pro obyvatele všech států kromě Kalifornie bude
                náhradním poskytovatelem rozhodčího řízení ADR Services, Inc. („ADR Services“) (
                <a href="https://www.adrservices.com/">https://www.adrservices.com/</a>), pouze
                pokud NAM nebude pro rozhodčí řízení k dispozici.
              </p>
              <p>
                Není-li touto Dohodou o rozhodčím řízení stanoveno jinak, bude NAM spravovat
                rozhodčí řízení podle svých „Comprehensive Dispute Resolution Rules and Procedures“,
                „Fees For Disputes When One of the Parties is a Consumer“ a „Mass Filing
                Supplemental Dispute Resolution Rules and Procedures“, platných v době podání návrhu
                na rozhodčí řízení u NAM, s vyloučením pravidel a postupů dovolujících hromadné či
                zástupné žaloby. Příslušná pravidla a postupy NAM jsou dostupné na{' '}
                <a href="https://www.namadr.com/">www.namadr.com</a> nebo na vyžádání e-mailem u
                obchodního oddělení National Arbitration and Mediation na{' '}
                <a href="mailto:commercial@namadr.com">commercial@namadr.com</a>.
              </p>
              <p>
                Není-li touto Dohodou o rozhodčím řízení stanoveno jinak, bude ADR Services
                spravovat rozhodčí řízení podle svých Arbitration Rules a General Fee Schedule
                platných v době podání návrhu na rozhodčí řízení u ADR Services, včetně pravidel pro
                „Mass Arbitrations“ (hromadná rozhodčí řízení), s vyloučením pravidel dovolujících
                hromadné či zástupné žaloby. Příslušná pravidla a postupy ADR Services jsou dostupné
                na <a href="http://www.adrservices.com">www.adrservices.com</a>.
              </p>
              <p>
                Pokud sporná částka nepřesahuje 10 000 USD a nepožadujete nápravu formou příkazu či
                zákazu nebo určovací nápravu, proběhne rozhodčí řízení výhradně na základě
                dokumentů, které vy a Discord předložíte rozhodci, ledaže rozhodce určí, že je nutné
                ústní jednání, nebo platné právo vyžaduje jiný postup. Pokud sporná částka přesahuje
                10 000 USD nebo je požadována určovací náprava nebo náprava formou příkazu či
                zákazu, může kterákoli Strana požádat (nebo rozhodce rozhodnout), aby se konalo
                jednání osobně, videokonferencí či telefonicky. Vy a Discord souhlasíte s
                přípustností návrhů na rozhodnutí věci bez úplného projednání (dispositive motions)
                v rozhodčím řízení.
              </p>
              <p>
                <strong>Náklady rozhodčího řízení.</strong> ADR Services a NAM stanovují poplatky za
                své služby a každý z poskytovatelů je zpřístupňuje. Pokud rozhodčí řízení proti vám
                zahájí Discord, uhradí všechny náklady s ním spojené, včetně celého poplatku za
                podání. Pokud rozhodčí řízení proti Discordu zahájíte vy, určí se náklady podle
                pravidel příslušného poskytovatele rozhodčího řízení v rozsahu dovoleném zákonem. Ve
                všech rozhodčích řízeních odpovídáte za veškeré další vzniklé náklady, včetně odměn
                advokátů a nákladů na znalce, ledaže zákon nebo pravidla poskytovatele rozhodčího
                řízení vyžadují něco jiného.
              </p>
              <p>
                Strany souhlasí, že poskytovatel rozhodčího řízení může podle svého uvážení upravit
                výši nebo splatnost administrativních či rozhodčích poplatků podle příslušných
                pravidel, považuje-li to za vhodné, pokud tím nezvýší vaše náklady. Vzdáváte se
                námitek proti takové úpravě poplatků. Strany také souhlasí, že napadení poplatků
                uložených poskytovatelem rozhodčího řízení kteroukoli Stranou v dobré víře
                nepředstavuje prodlení, vzdání se práv ani porušení této části, dokud se taková
                námitka projednává před poskytovatelem rozhodčího řízení, rozhodcem a/nebo
                příslušným soudem. Po dobu projednávání námitky se staví běh všech lhůt splatnosti
                těchto poplatků.
              </p>
              <p>
                <strong>Nabídka na vydání rozsudku.</strong> Nejméně 14 dnů před termínem rozhodčího
                jednání může kterákoli Strana druhé Straně doručit písemnou nabídku, aby byl za
                stanovených podmínek vydán rozsudek. Pokud druhá Strana nabídku nepřijme a následně
                nezíská příznivější rozhodčí nález, nebude jí přiznána náhrada nákladů vzniklých po
                nabídce a uhradí náklady nabízející Strany vzniklé od okamžiku nabídky.
              </p>
              <p>
                <strong>Rozhodčí nález.</strong> Rozhodnutí rozhodce bude písemné a závazné pro vás
                i Discord. Rozsudek k jeho vynucení může vydat kterýkoli příslušný soud.
              </p>
              <p>
                Není-li v této Dohodě o rozhodčím řízení výslovně stanoveno jinak, má rozhodce,
                nikoli federální, státní či místní soud nebo správní orgán, výlučnou pravomoc
                rozhodovat všechny Spory vzniklé z výkladu, použitelnosti, vymahatelnosti nebo
                vzniku těchto podmínek či s nimi související. To zahrnuje tvrzení, že jsou všechny
                tyto podmínky nebo jejich část neplatné či zrušitelné, otázku, zda nárok podléhá
                rozhodčímu řízení, i spory o úhradu poplatků poskytovatele rozhodčího řízení či
                rozhodce (včetně načasování plateb a prostředků nápravy při nezaplacení). Rozhodce
                musí dodržovat tyto podmínky a může přiznat stejnou náhradu škody a nápravu jako
                soud. Rozhodce může podle pravidel a postupů poskytovatele rozhodčího řízení ukládat
                sankce za zjevně bezdůvodné či nepřípustné nároky nebo podání, která podle jeho
                závěru nebyla učiněna v dobré víře, za nedodržení této Dohody o rozhodčím řízení
                některou ze Stran či za nároky uplatněné jménem žalobce, který není stranou této
                dohody. Žádný rozhodčí nález ani rozhodnutí nebude mít účinek překážky již
                rozhodnuté věci ohledně otázek či nároků v jakémkoli sporu s osobou, která není
                jmenovanou stranou rozhodčího řízení.
              </p>
              <p>
                <strong>Místo rozhodčího řízení.</strong> S výjimkou ustanovení níže v části
                „Hromadná podání“ platí, že pokud máte bydliště ve Spojených státech a zahájíte
                rozhodčí řízení, proběhne v okrese vašeho bydliště. Není-li v tomto okrese rozhodce
                k dispozici, proběhne na nejbližším dostupném místě rozhodčího řízení v daném státě.
                Pokud rozhodčí řízení zahájí Discord, a rovněž v případě obyvatel Kanady, zahájí se
                řízení v okrese New Castle ve státě Delaware, USA, ledaže se vy a Discord dohodnete
                jinak nebo určený rozhodce na základě písemné námitky zjistí, že by toto místo bylo
                pro některou Stranu nepřiměřeně zatěžující. V takovém případě může rozhodce zvolit
                jiné místo navzdory původní volbě kterékoli Strany. Pro každé rozhodčí řízení
                souhlasí Strany s osobní jurisdikcí federálních či státních soudů v okrese New
                Castle ve státě Delaware pro účely nařízení rozhodčího řízení, přerušení soudního
                řízení do skončení rozhodčího řízení nebo potvrzení, změny či zrušení rozhodčího
                nálezu či vydání rozsudku na jeho základě. V souvislosti s takovým soudním řízením
                se Strany vzdávají všech jinak dostupných námitek proti pravomoci a místní
                příslušnosti. Strany souhlasí, že rozhodčí řízení může probíhat výhradně
                prostřednictvím videa, pokud je takové uspořádání pro jmenovaného rozhodce
                přijatelné.
              </p>
              <p>
                <strong>Hromadná podání.</strong>
              </p>
              <p>
                Pro rozhodčí řízení před NAM (nebo náhradním poskytovatelem za NAM, na kterém se
                Strany dohodnou nebo kterého určí soud, nebude-li NAM z jakéhokoli důvodu k
                dispozici) platí následující pravidla pro zvýšení efektivity správy a řešení
                rozhodčích řízení. Pokud je u NAM proti Discordu v přiměřeně krátkém časovém odstupu
                podáno 100 nebo více podobných návrhů na rozhodčí řízení (uplatňujících stejné nebo
                podstatně podobné skutečnosti či nároky a požadujících stejnou nebo podstatně
                podobnou nápravu), předložených stejnými advokátními kancelářemi či organizacemi
                nebo s jejich pomocí či koordinací („Hromadné podání“), Strany souhlasí: (i) se
                správou Hromadného podání v dávkách po 100 návrzích (zbude-li po tomto rozdělení
                méně než 100 návrhů, budou tvořit poslední dávku), přičemž se najednou podává,
                zpracovává a rozhoduje pouze jedna dávka; (ii) s určením jednoho rozhodce pro každou
                dávku; (iii) s příslušnými poplatky včetně souvisejícího snížení poplatků určeného
                NAM; (iv) s tím, že 10 % předpokládaného celkového počtu dávek Hromadného podání
                může probíhat současně podle tohoto ustanovení o dávkách a Dohody o rozhodčím
                řízení, ale žádné další návrhy zahrnuté do Hromadného podání nesmějí být podány,
                zpracovány ani rozhodnuty před skončením níže popsaného „Mediačního období“ a před
                podáním, zpracováním a rozhodnutím předchozí dávky 100 návrhů; (v) s tím, že
                poplatky související s návrhem na rozhodčí řízení zahrnutým do Hromadného podání,
                včetně poplatků hrazených Discordem a žalobci, budou splatné až po zařazení vašeho
                návrhu do dávky řízení, která bude řádně určena k podání, zpracování a rozhodnutí; a
                (vi) s tím, že tento postup v dávkách, z nichž každá zahrnuje 100 návrhů, bude
                pokračovat, dokud nebude každý návrh (včetně vašeho) rozhodnut nebo jinak vyřešen.
                Výběr rozhodce pro každou dávku se řídí příslušnými pravidly a postupy NAM a podléhá
                právům na odmítnutí rozhodce podle platného práva daného státu, pokud tato zákonná
                práva přesahují práva podle pravidel NAM. Bez ohledu na část „Místo rozhodčího
                řízení“ určí u řízení podléhajících této části „Hromadná podání“ místo řízení
                rozhodce.{' '}
                <strong>
                  Souhlasíte, že budete v dobré víře spolupracovat s Discordem, rozhodcem a
                  poskytovatelem rozhodčího řízení na zavedení tohoto „dávkového postupu“ nebo
                  obdobného postupu pro efektivní vyřešení nároků, včetně úhrady společných
                  snížených poplatků za každou dávku nároků, které podle svého uvážení stanoví NAM.
                </strong>{' '}
                Neshody Stran o použitelnosti tohoto ustanovení, o procesu či postupu rozdělování do
                dávek nebo o tom, zda jsou návrhy řádně podány a mohou být do dávky zařazeny,
                rozhodne procesní rozhodce jmenovaný NAM. Toto ustanovení o „Rozhodčím řízení v
                dávkách“ nelze vykládat tak, že zvyšuje počet nároků nutný pro použití pravidel NAM
                Mass Filing Supplemental Dispute Resolution Rules and Procedures nebo dovoluje
                jakýkoli druh skupinového rozhodčího řízení (class arbitration). Pokud je váš návrh
                zahrnut do Hromadného podání, běh příslušných promlčecích lhůt pro vaše nároky
                zůstane pozastaven, dokud nebude váš návrh na rozhodčí řízení rozhodnut, vzat zpět
                nebo vyřešen smírem.
              </p>
              <p>
                U rozhodčích řízení před NAM budou výsledky první zcela rozhodnuté dávky návrhů
                předány mediátorovi NAM, který bude vybrán ze skupiny 5 mediátorů navržených NAM.
                Discord i právní zástupce zbývajících žalobců mohou každý odmítnout jednoho
                mediátora a poté seřadit zbývající mediátory podle preference; vybrán bude mediátor
                s nejvyšším společným hodnocením. Vybraný mediátor se pokusí zprostředkovat vyřešení
                zbývajících návrhů v Hromadném podání. Po předání výsledků první dávky mediátorovi
                budou mít Discord, zbývající žalobci a jejich právní zástupci společně s mediátorem
                90 dnů („<strong>Mediační období</strong>“) k dohodě na řešení nebo věcném postupu
                řešení zbývajících návrhů. Pokud Strany během Mediačního období nevyřeší zbývající
                návrhy a nedohodnou se na postupu jejich řešení dalšími rozhodčími řízeními, může
                Discord nebo kterýkoli zbývající žalobce z rozhodčího procesu vystoupit („Odmítnutí
                hromadného rozhodčího řízení“) a nechat návrh či návrhy projednat příslušným soudem.
                Oznámení o Odmítnutí hromadného rozhodčího řízení se podává písemně do 60 dnů od
                skončení Mediačního období. Pokud Discord ani zbývající žalobci tuto možnost
                nevyužijí a nedohodnou se na postupu řešení zbývajících návrhů dalšími rozhodčími
                řízeními, bude rozhodčí řízení pokračovat v dávkách. Není-li podáno oznámení o
                Odmítnutí hromadného rozhodčího řízení, budou řízení pokračovat v pořadí daném
                pořadovými čísly přidělenými návrhům v Hromadném podání.
              </p>
              <p>
                <strong>Rozhodčí řízení u ADR Services.</strong> Není-li v tomto ustanovení
                stanoveno jinak, odstavce části „Hromadná podání“ se nepoužijí na rozhodčí řízení u
                ADR Services. Strany souhlasí, že tato řízení se řídí pravidly ADR Services včetně
                pravidel pro „Mass Filings“ (hromadná podání). U Hromadných podání před ADR Services
                Strany rovněž souhlasí, že po vyřešení 10 % celkového počtu případů zahrnutých do
                Hromadných podání se Discord a zbývající žalobci zúčastní mediace. Pokud po jejím
                skončení Strany nevyřeší zbývající návrhy a nedohodnou se na postupu jejich řešení
                dalšími rozhodčími řízeními, může Discord nebo kterýkoli zbývající žalobce z
                rozhodčího procesu vystoupit.
              </p>
              <p>
                <strong>Odmítnutí dohody (opt-out).</strong> Tuto{' '}
                <strong>Dohodu o rozhodčím řízení</strong> – a žádné jiné ustanovení těchto podmínek
                – můžete odmítnout zasláním oznámení e-mailem na{' '}
                <a href="mailto:arbitration-opt-out@discord.com">arbitration-opt-out@discord.com</a>{' '}
                do 30 dnů od 29. září 2025 nebo od první registrace vašeho účtu Discord, podle toho,
                co nastane později. Jinak budete povinni řešit Spory rozhodčím řízením podle těchto
                odstavců. Pokud Dohodu o rozhodčím řízení odmítnete, nebude jí vázán ani Discord a
                mezi vámi a Discordem nebude existovat žádná dohoda o rozhodčím řízení, ať už podle
                těchto, nebo dřívějších rozhodčích ustanovení. Pokud Dohodu o rozhodčím řízení
                odmítnete, můžete uplatnit právo na soudní řízení před porotou nebo soudcem v
                rozsahu dovoleném platným právem; na dosud neuplatněné nároky se však nebude
                vztahovat žádná dřívější dohoda o rozhodčím řízení. Odmítnutím Dohody o rozhodčím
                řízení neodmítáte žádné jiné ustanovení těchto podmínek a souhlasíte, že budete
                vázáni všemi ostatními ustanoveními, která zůstávají účinná v rozsahu dovoleném
                zákonem.
              </p>
              <p>
                <strong>Výjimky.</strong> Vy nebo Discord můžete i nadále individuálně uplatnit
                způsobilé nároky výhradně u soudu pro drobné nároky ve Spojených státech, jde-li o
                Spory a žaloby spadající do jeho příslušnosti, bez ohledu na to, jaké fórum
                podávající Strana původně zvolila. Vy i Discord souhlasíte s místní příslušností a
                osobní jurisdikcí tohoto soudu. Výlučnou pravomoc rozhodovat, zda Spor řádně spadá
                do příslušnosti soudu pro drobné nároky, má tento soud, nikoli rozhodce ani
                poskytovatel rozhodčího řízení. Rozhodčímu řízení dále nepodléhají Spory týkající se
                patentů, autorských práv, osobnostních práv autorů, ochranných známek a obchodních
                tajemství ani nároky z pirátství nebo neoprávněného používání našich služeb.
                Kterákoli Strana může u příslušného soudu rovněž požadovat určovací rozsudek nebo
                jinou nápravu podle zásad ekvity ohledně toho, zda jsou nároky Strany promlčené nebo
                zda je lze uplatnit u soudu pro drobné nároky. Požadování takové nápravy
                nepředstavuje vzdání se práva Strany na rozhodčí řízení podle této dohody. Všechna
                zahájená rozhodčí řízení související se žalobou podanou podle tohoto odstavce budou
                automaticky přerušena (a běh příslušných promlčecích lhůt pozastaven) do rozhodnutí
                o takové žalobě. Kterákoli Strana si může zvolit, aby o tom, zda stěžující si Strana
                splnila výše uvedený postup Neformálního řešení sporů, rozhodl před zahájením
                rozhodčího řízení soud; jinak bude tato otázka rozhodnuta v rozhodčím řízení.
              </p>
              <p>
                <strong>Vzdání se skupinového rozhodčího řízení a kolektivní nápravy.</strong>{' '}
                JSTE-LI REZIDENTEM USA, NENÍ-LI ZDE STANOVENO JINAK A NEDOHODNE-LI SE DISCORD JINAK
                V SAMOSTATNÉM PÍSEMNÉM UJEDNÁNÍ, SOUHLASÍTE VY I DISCORD S TÍM, ŽE PROTI SOBĚ MŮŽETE
                UPLATŇOVAT NÁROKY POUZE VLASTNÍM JMÉNEM, NIKOLI JAKO ŽALOBCE NEBO ČLEN SKUPINY V
                JAKÉMKOLI ZAMÝŠLENÉM HROMADNÉM ČI ZÁSTUPNÉM ŘÍZENÍ. ROZHODCE SMÍ PŘIZNAT NÁPRAVU
                POUZE VE PROSPĚCH JEDNOTLIVÉ STRANY, KTERÁ JI POŽADUJE, A POUZE V ROZSAHU NEZBYTNÉM
                K VYŘEŠENÍ JEJÍHO INDIVIDUÁLNÍHO NÁROKU. Pokud Discord se sloučením souhlasí, může
                být sloučení povoleno dohodou nebo nařízeno procesním rozhodcem či rozhodcem pro
                předběžné otázky u ADR Services nebo NAM a vy souhlasíte, že takové sloučení
                přijmete. V případech sloučených podle tohoto odstavce se náprava může vztahovat na
                sloučené nároky.
              </p>
              <p>
                Pokud bude odstavec „Vzdání se skupinového rozhodčího řízení a kolektivní nápravy“,
                odstavec „Hromadná podání“ nebo pravidla ADR Services pro hromadná rozhodčí řízení
                shledán nevymahatelným, bude část „Dohoda o rozhodčím řízení“ neplatná a neúčinná.
                Pokud bude jiné ustanovení shledáno neplatným, nevymahatelným či nezákonným nebo
                jinak v rozporu s pravidly příslušného poskytovatele rozhodčího řízení, zbytek této
                Dohody o rozhodčím řízení zůstane účinný a bude vykládán podle svého znění, jako by
                neplatné, nevymahatelné, nezákonné či rozporné ustanovení neobsahoval.
              </p>
              <p>
                Tato podčást vám ani Discordu nebrání účastnit se smíru zahrnujícího celou skupinu
                nároků.
              </p>
              <p>
                <strong>Změny této Dohody o rozhodčím řízení:</strong> Discord oznámí datum každé
                podstatné změny této Dohody o rozhodčím řízení 30 dnů předem. Změny nabudou
                účinnosti třicátým dnem a budou se vztahovat na všechny dosud neuplatněné nároky bez
                ohledu na okamžik jejich vzniku. Pokud s těmito podmínkami souhlasíte nejpozději
                třicátý den nebo po třicátém dni dále používáte web, souhlasíte, že všechny
                neuplatněné nároky, o kterých Discord nemá skutečnou vědomost na základě postupu
                Neformálního řešení sporů, podléhají upravené doložce. Pokud takové změny odmítnete
                odmítnutím Dohody o rozhodčím řízení, můžete uplatnit právo na soudní řízení před
                porotou nebo soudcem v rozsahu dovoleném platným právem, ale bude se mít za to, že
                mezi vámi a Discordem neexistuje rozhodčí dohoda, a žádná dřívější dohoda o
                rozhodčím řízení Sporů podle předchozí verze Dohody o rozhodčím řízení se nebude
                vztahovat na dosud neuplatněné nároky. Pokud Discord tuto Dohodu o rozhodčím řízení
                změní po dni, kdy jste ji poprvé přijali (nebo přijali její pozdější změny),
                souhlasíte, že vaše další používání produktů nebo služeb Discordu po uplynutí 30 dnů
                od změny bude považováno za její přijetí. Pokud se změnou nesouhlasíte, můžete
                Dohodu o rozhodčím řízení odmítnout zasláním oznámení na{' '}
                <a href="mailto:arbitration-opt-out@discord.com">arbitration-opt-out@discord.com</a>{' '}
                před uplynutím této 30denní lhůty.
              </p>
            </SectionWrapper>

            {/* Section 17 */}
            <SectionWrapper number="17" title="Další důležité věci">
              <p>
                Máte určitá práva, která ze zákona nelze těmito podmínkami omezit, a v žádném
                případě nemáme v úmyslu tato práva v těchto podmínkách omezovat. Výraz „včetně“
                použitý v těchto podmínkách znamená „včetně, nikoli však výhradně“.
              </p>
              <p>
                <strong>
                  <em>Celá dohoda.</em>
                </strong>{' '}
                Tyto podmínky pokrývají celou dohodu mezi vámi a Discord pro vaše používání našich
                služeb.
              </p>
              <p>
                <strong>
                  <em>Dodatečné podmínky.</em>
                </strong>{' '}
                Pokud se na naše produkty nebo služby vztahují dodatečné podmínky, budou mít
                dodatečné podmínky přednost v rozsahu jakéhokoli konfliktu s těmito podmínkami s
                ohledem na vaše používání daného produktu nebo služby. Další informace o našich
                službách pro uživatele v Rakousku jsou k dispozici{' '}
                <a href="https://discord.com/terms/eecc-addendum">zde</a>.
              </p>
              <p>
                <strong>
                  <em>Hlášení chyb.</em>
                </strong>{' '}
                Podporujeme odpovědné hlášení bezpečnostních zranitelností. Chcete-li nahlásit
                bezpečnostní problém, navštivte prosím{' '}
                <a href="https://discord.com/security">https://discord.com/security</a>.
              </p>
              <p>
                <strong>
                  <em>Kontrola vývozu.</em>
                </strong>{' '}
                Souhlasíte s tím, že budete dodržovat všechny platné zákony a omezení týkající se
                dovozu, vývozu a zpětného vývozu, včetně, ale nikoli výhradně, zákonů Evropské unie
                a jejích členských států, předpisů o správě vývozu Ministerstva obchodu USA ("EAR")
                a ekonomických sankcí udržovaných Úřadem pro kontrolu zahraničních aktiv USA
                ("OFAC") a Mezinárodních předpisů o obchodu se zbraněmi ("ITAR"), a nebudete
                používat služby k způsobení porušení takových zákonů nebo předpisů. Souhlasíte s
                tím, že nebudete používat naše služby k ukládání nebo distribuci obsahu, který
                podléhá kontrolám vývozu, pokud jste nezískali všechna požadovaná vládní povolení k
                vývozu. Dále prohlašujete a zaručujete, že nejste na žádném vládním seznamu
                zakázaných nebo omezených stran nebo jinak nepodléháte ekvivalentním omezením, jak
                je uvedeno ve výše uvedených zákonech a předpisech nebo v zákonech vaší země.
                Nemůžete stahovat nebo používat naše služby, pokud se nacházíte v zemi nebo regionu,
                na který se vztahuje embargo vlády USA nebo EU (včetně Kuby, Íránu, Severní Koreje,
                Sýrie a krymského regionu), pokud takové použití není autorizováno Spojenými státy a
                dalšími příslušnými orgány.
              </p>
              <p>
                <strong>
                  <em>Zřeknutí se, oddělitelnost a postoupení.</em>
                </strong>{' '}
                Pokud nedodržíte tyto podmínky a my okamžitě nezasáhneme, neznamená to, že se
                vzdáváme jakýchkoli našich zákonných práv (například zasáhnout v budoucnu). Pokud se
                jakákoli část těchto podmínek ukáže jako neplatná nebo nevymahatelná na základě
                rozhodnutí jakéhokoli soudu nebo příslušného orgánu, zbytek těchto podmínek nebude
                ovlivněn. Bez našeho písemného souhlasu nemůžete tyto podmínky postoupit nikomu
                jinému. My můžeme postoupit naše práva kterékoli z našich přidružených společností
                nebo dceřiných společností nebo jakémukoli nástupci v zájmu jakéhokoli podnikání
                spojeného s našimi službami.
              </p>
              <p>
                <strong>
                  <em>Přetrvání.</em>
                </strong>{' '}
                Jakákoli část těchto podmínek, která by svou povahou měla přetrvat po ukončení
                těchto podmínek, přetrvá. Jak je povoleno podle platného práva, to zahrnuje, ale
                nemusí se omezovat na následující:
              </p>
              <ul role="list">
                <li>
                  Naše práva{' '}
                  <a href="https://support.discord.com/hc/en-us/articles/5431812448791-How-long-Discord-keeps-your-information">
                    uchovávat a zobrazovat určitá data
                  </a>
                  ;
                </li>
                <li>Jakékoli dlužné částky zůstanou splatné;</li>
                <li>
                  Jakékoli závazky k odškodnění (podle potřeby), jako jsou ty uvedené v sekci
                  "Odškodnění";
                </li>
                <li>Jakékoli zřeknutí se záruk, jako jsou ty v sekci "Služby 'TAK JAK JSOU'";</li>
                <li>
                  Jakékoli platné omezení odpovědnosti, jako jsou ta v sekci "Omezení odpovědnosti";
                </li>
                <li>
                  Jakákoli ustanovení o řešení sporů, včetně arbitrážní dohody, jako jsou ta v sekci
                  "Řešení sporů mezi vámi a Discord".
                </li>
              </ul>
              <p>
                <strong>
                  <em>Aktualizace těchto podmínek.</em>
                </strong>{' '}
                Můžeme se rozhodnout aktualizovat tyto podmínky: (1) aby odrážely změny našich
                služeb nebo našeho podnikání, (2) z právních nebo regulačních důvodů, (3) aby se
                zabránilo zneužití našich služeb nebo (4) abychom lépe chránili uživatele našich
                služeb nebo jim lépe sloužili. Pokud tyto změny podstatně ovlivní vaše používání
                Discord nebo vaše zákonná práva, poskytneme vám přiměřené předběžné oznámení (pokud
                aktualizace nejsou naléhavé). Pokud budete pokračovat v používání našich služeb
                poté, co změny vstoupily v platnost, znamená to, že s těmito změnami souhlasíte.
                Pokud nesouhlasíte, musíte přestat používat naše služby.
              </p>
              <p>
                <strong>
                  <em>Apple App Store.</em>
                </strong>{' '}
                Pokud si stáhnete aplikaci Discord z Apple App Store nebo používáte naši aplikaci na
                zařízení iOS, platí pro vás níže uvedený odstavec.
              </p>
              <p>
                Tyto podmínky udělují nepřevoditelnou licenci k používání aplikace Discord na
                jakémkoli produktu Apple/Mac, který můžete vlastnit nebo ovládat, a jak to povolují
                zásady společnosti Apple. Apple nemá žádnou povinnost poskytovat jakékoli služby
                údržby a podpory s ohledem na aplikaci Discord. Pokud aplikace nesplňuje jakoukoli
                platnou záruku, můžete upozornit Apple a Apple vám vrátí kupní cenu aplikace (pokud
                existuje) a v maximálním rozsahu povoleném platným právem nebude mít Apple žádnou
                další záruční povinnost vůči aplikaci Discord. Apple není odpovědný za řešení
                jakýchkoli nároků vámi nebo jakékoli třetí strany týkajících se aplikace Discord
                nebo vašeho držení a používání, včetně, ale nikoli výhradně: (i) nároků na
                odpovědnost za výrobek; (ii) jakéhokoli nároku, že aplikace Discord nesplňuje
                jakýkoli platný právní nebo regulační požadavek; a (iii) nároků vzniklých podle
                ochrany spotřebitele nebo podobné legislativy. Apple není odpovědný za vyšetřování,
                obhajobu, vyrovnání a zprostění jakéhokoli nároku třetí strany, že vaše držení a
                používání aplikace Discord porušuje práva duševního vlastnictví této třetí strany.
                Apple a jeho dceřiné společnosti jsou oprávněnými třetími stranami těchto podmínek a
                po přijetí podmínek bude mít Apple právo (a bude se mít za to, že přijalo právo)
                vymáhat tyto podmínky proti vám jako oprávněná třetí strana. Prohlašujete a
                zaručujete, že (i) se nenacházíte v zemi, která je předmětem embarga vlády USA nebo
                která byla vládou USA označena jako země podporující terorismus; a (ii) nejste
                uvedeni na žádném seznamu zakázaných nebo omezených stran vlády USA. Při používání
                aplikace Discord musíte také dodržovat všechny platné podmínky služby třetích stran.
              </p>

              <h2>Jak nás kontaktovat</h2>
              <p>
                Máte-li dotazy k těmto podmínkám nebo nás v souvislosti s nimi chcete kontaktovat,
                napište nám na <a href="mailto:privacy@discord.com">privacy@discord.com</a>, není-li
                v těchto podmínkách uvedeno jinak. Můžeme vám zasílat elektronickou komunikaci
                související s našimi službami. Tam, kde to bude vyžadováno, získáme váš souhlas před
                zasláním přímého marketingu a usnadníme vám odhlášení.
              </p>
              <p>
                O technickou podporu nebo jinou pomoc s našimi službami můžete požádat{' '}
                <a href="https://support.discord.com/hc/en-us/requests/new">zde</a>.
              </p>
              <p>
                Discord Inc. sídlí na adrese 444 De Haro Street #200, San Francisco, CA 94107, USA.
                Discord Netherlands B.V. sídlí na adrese Schiphol Boulevard 195, 1118 BG Schiphol,
                Nizozemsko.
              </p>
            </SectionWrapper>
          </div>
        </>
      )}
    </DiscordDocument>
  )
}

export default DiscordTermsPage
